/**
 * Visit Recorder — public facade.
 *
 * Spec: /Users/omerklein/visit-recording-spec-v2.md
 *
 * Lifecycle:
 *   await Recorder.start(visitId, deviceInfo)  // call on clock-in
 *     -> POSTs /recording/init, opens session, starts MonotonicClock + EventLogger
 *   Recorder.addEvent(eventType, payload?, severity?)
 *   Recorder.notifySkip("camera_open" | ...)
 *   Recorder.clearSkip("camera_open")
 *   await Recorder.heartbeat({batteryLevel, networkType})
 *   await Recorder.stop("submit")  // call on submit-for-review
 *     -> closes session and POSTs /recording/finalize
 *
 * Native module integration (Phase 4/5) lives in a separate file and is
 * driven via SkipRules.onChange + lifecycle hooks. This module is JS-only.
 */
import { getApiBase, getValidToken } from "./api-bridge";
import { DeviceHealth } from "./DeviceHealth";
import { EventLogger } from "./EventLogger";
import { MonotonicClock } from "./MonotonicClock";
import { SkipRules } from "./SkipRules";
import type {
    DeviceInfo,
    EventSeverity,
    InitRecordingResponse,
    RecorderState,
    SkipReason
} from "./types";
import { UploadQueue } from "./UploadQueue";

let state: RecorderState = {
    started: false,
    visitId: null,
    recordingId: null,
    sessionId: null,
    sessionIndex: null,
    sessionStartMs: null,
    deviceSuspended: false
};

let skipUnsubscribe: (() => void) | null = null;

// Concurrency-safe start: if start() is called twice while the first init
// is still in flight (e.g. handleClockIn + auto-resume effect both fire
// before state.started flips), the second call returns the same in-flight
// promise instead of issuing a duplicate POST /init.
let startInFlight: Promise<InitRecordingResponse | null> | null = null;

async function postJson<T>(path: string, body: unknown): Promise<T | null> {
    const token = await getValidToken();
    if (!token) {
        return null;
    }
    const res = await fetch(`${getApiBase()}${path}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
    });
    if (!res.ok) {
        return null;
    }
    return (await res.json()) as T;
}

function getDeviceId(): string {
    // V1: stable per-install pseudo-id from a global token. Native modules
    // will replace this with iOS identifierForVendor / Android ANDROID_ID.
    // For events-only mode this is fine — collisions across reinstall are rare
    // and the server treats these as ephemeral health rows.
    const g = globalThis as unknown as { __TASKHEALTH_DEVICE_ID__?: string };
    if (g.__TASKHEALTH_DEVICE_ID__) {
        return g.__TASKHEALTH_DEVICE_ID__;
    }
    const id = `js-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    g.__TASKHEALTH_DEVICE_ID__ = id;
    return id;
}

export const Recorder = {
    state(): Readonly<RecorderState> {
        return state;
    },

    async start(visitId: number, deviceInfo?: DeviceInfo): Promise<InitRecordingResponse | null> {
        if (state.started && state.visitId === visitId) {
            return null; // idempotent
        }
        if (startInFlight) {
            return startInFlight;
        }
        startInFlight = (async () => {
            try {
                const init = await postJson<InitRecordingResponse>(
                    `/visits/${visitId}/recording/init`,
                    {
                        deviceId: getDeviceId(),
                        deviceInfo
                    }
                );
                if (!init) {
                    return null;
                }

                if (init.deviceSuspended) {
                    DeviceHealth.record(true, init.deviceSuspendedUntil);
                    state = {
                        started: false,
                        visitId,
                        recordingId: null,
                        sessionId: null,
                        sessionIndex: null,
                        sessionStartMs: null,
                        deviceSuspended: true
                    };
                    return init;
                }

                DeviceHealth.record(false, null);
                MonotonicClock.startSession();
                EventLogger.start(visitId, init.sessionId);

                state = {
                    started: true,
                    visitId,
                    recordingId: init.recordingId,
                    sessionId: init.sessionId,
                    sessionIndex: init.sessionIndex,
                    sessionStartMs: Date.now(),
                    deviceSuspended: false
                };

                skipUnsubscribe = SkipRules.onChange((reasons) => {
                    Recorder.addEvent(reasons.length > 0 ? "capture_paused" : "capture_resumed", {
                        reasons
                    });
                });

                Recorder.addEvent("session_start", { sessionIndex: init.sessionIndex });
                return init;
            } finally {
                startInFlight = null;
            }
        })();
        return startInFlight;
    },

    async stop(
        endedReason:
            | "submit"
            | "backgrounded_timeout"
            | "killed_recovered"
            | "battery"
            | "cancelled"
    ): Promise<void> {
        if (!state.started || !state.visitId || !state.sessionId) {
            return;
        }
        Recorder.addEvent("session_end", { endedReason });
        await EventLogger.flush();
        await postJson(`/visits/${state.visitId}/recording/close-session`, {
            sessionId: state.sessionId,
            endedReason
        });
        if (endedReason === "submit") {
            await postJson(`/visits/${state.visitId}/recording/finalize`, {});
        }
        if (skipUnsubscribe) {
            skipUnsubscribe();
            skipUnsubscribe = null;
        }
        EventLogger.stop();
        MonotonicClock.reset();
        state = {
            started: false,
            visitId: null,
            recordingId: null,
            sessionId: null,
            sessionIndex: null,
            sessionStartMs: null,
            deviceSuspended: false
        };
    },

    addEvent(
        eventType: string,
        payload: Record<string, unknown> = {},
        severity: EventSeverity = "info"
    ): void {
        if (!state.started) {
            return;
        }
        EventLogger.addEvent(eventType, payload, severity);
    },

    notifySkip(reason: SkipReason): void {
        SkipRules.notify(reason);
    },

    clearSkip(reason: SkipReason): void {
        SkipRules.clear(reason);
    },

    async heartbeat(opts: {
        deviceBatteryLevel?: number;
        networkType?: "wifi" | "cellular" | "none";
    }): Promise<void> {
        if (!state.started || !state.visitId || !state.sessionId) {
            return;
        }
        await postJson(`/visits/${state.visitId}/recording/heartbeat`, {
            sessionId: state.sessionId,
            deviceBatteryLevel: opts.deviceBatteryLevel,
            networkType: opts.networkType
        });
    },

    pendingUploads(): number {
        return UploadQueue.pendingCount();
    }
};

export type { DeviceInfo, EventSeverity, RecorderState, SkipReason } from "./types";
